import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Avatar,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Container,
  Skeleton,
  Divider,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Popover,
  Stack,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  MedicalServices as MedicalIcon,
  Vaccines as VaccineIcon,
  LocalHospital as HospitalIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  Pets as PetsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Share as ShareIcon,
  Warning as WarningIcon,
  Place as PlaceIcon,
  CalendarToday as CalendarIcon,
  History as HistoryIcon,
  FolderOpen as PatientFilesIcon,
  AttachFile as AttachFileIcon,
  Healing as HealingIcon,
  Note as NoteIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatDistanceToNow } from 'date-fns';
import { INSTANT_FORMATS, formatCalendarDate } from '../../utils/dateUtils';
import { useClinicTimezone, useFormatInstant } from '../../hooks/useClinicTimezone';
import { groupMedicationsByStartDay, medicalRecordDateKind, pickCogginsSummary, vaccinationSummary as summarizeVaccinations } from './chartDates';
import { isCogginsLabResultRow, isCogginsName, isPositiveCogginsResult } from '../../utils/coggins';
import { horseApi, medicalRecordsApi, appointmentApi, Horse, MedicalRecord, billingApi, InvoiceStats, treatmentSheetApi } from '../../utils/api';
import { useAuth } from '../../auth/useAuth';
import { useRBAC } from '../../auth/useRBAC';
import { patientChartControls, patientChartPanels } from '../../auth/pageControls';
import { CHART_PANELS, noAccessMessage } from '../../auth/screenAccess';
import NoAccess from '../../components/NoAccess';
import DischargeSummarySimplified from '../../components/DischargeSummary/DischargeSummarySimplified';
import HorsePhotoAvatar from '../../components/HorsePhotoAvatar';
import SoapNoteDialog from '../../components/SoapNote/SoapNoteDialog';
import ShareRecordsDialog from '../../components/StableDocs/ShareRecordsDialog';
import ShareSentDetail from '../../components/StableDocs/ShareSentDetail';
import { useStableDocsShares } from '../../hooks/useStableDocsShares';
import SoapNoteCard from '../../components/SoapNote/SoapNoteCard';
import PatientFilesTab from '../../components/PatientFiles/PatientFilesTab';
import { usePatientFiles } from '../../components/PatientFiles/usePatientFiles';
import { countSoapAttachmentsByNote } from '../../components/PatientFiles/patientFiles';
import { soapNotesService } from '../../services/soapNotesService';
import { SoapNote } from '../../types/soap';
// Imaging studies + requests now live entirely inside LabsDiagnosticsPanel.
// (Legacy ImagingRequestDialog deleted; ImagingStudyCard moved to the panel.)
import UnallocatedSuggestionBanner from '../../components/Inbox/UnallocatedSuggestionBanner';
import MedicationDialog from '../../components/Medication/MedicationDialog';
import MedicationCard from '../../components/Medication/MedicationCard';
import LogRefillRequestDialog from '../RefillRequests/LogRefillRequestDialog';
import { medicationService } from '../../services/medicationService';
import { inventoryService } from '../../services/inventoryService';
import ProcedureDialog from '../../components/Procedure/ProcedureDialog';
import ProcedureCard from '../../components/Procedure/ProcedureCard';
import VaccinationDialog from '../../components/Vaccination/VaccinationDialog';
import EditVaccinationDialog from '../../components/Vaccination/EditVaccinationDialog';
import VaccinationCard from '../../components/Vaccination/VaccinationCard';
import PatientVaccinationsTab from '../../components/Vaccination/PatientVaccinationsTab';
import { VaccinationService, toCreateVaccinationDto } from '../../services/vaccinationService';
import { ProcedureService } from '../../services/procedureService';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import ClientCommunicationDialog, { ClientCommunication } from '../../components/ClientCommunication/ClientCommunicationDialog';
import VisitNoteDialog from '../../components/VisitNote/VisitNoteDialog';
import ClientCommunicationList from '../../components/ClientCommunication/ClientCommunicationList';
import { clientCommunicationService } from '../../services/clientCommunicationService';
import { DischargeSummaryService } from '../../services/dischargeSummaryService';
import api from '../../utils/api';
import { formatDollars } from '../../utils/money';
import { formatBreed } from '../../utils/breed';
import AlertManagementDialog from '../../components/AlertManagementDialog';
import LabsDiagnosticsPanel from '../../components/LabsDiagnosticsPanel';
import ProblemListPanel from '../../components/ProblemListPanel';
import RecallRemindersPanel from '../../components/RecallRemindersPanel';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import { useSnackbar } from 'notistack';
import ChartLayoutToggle from './ChartLayoutToggle';
import { useChartLayout } from './useChartLayout';
import { markPatientViewed } from '../PatientList/recentlyViewed';
import { withOfflineFallback, skipWhenOffline, readHorse, readByHorse, readActiveByHorse, readInvoicesPageByHorse, readHorseInvoiceStats, executeWrite, executeUpdate, executeDelete, OfflineWriteBlockedError, useIsOffline } from '../../offline';
import PatientHeaderCard from '../../components/PatientHeaderCard';
import HorseFormDialog from '../../components/HorseFormDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const HorseProfileClassic: React.FC = () => {
  const { horseId } = useParams<{ horseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { layout, setLayout } = useChartLayout();
  const rbac = useRBAC();
  const { permissionsPending, claims } = rbac;
  // The chart's write controls and its per-panel READ gates, from the one tested map shared
  // with HorseProfileModern (INV-108 (b)+(d)). Both go through `controlAnswerKnown`, so nothing
  // is hidden and no panel is refused until the permission answer is actually in.
  const can = patientChartControls(rbac);
  const isOffline = useIsOffline();
  const panels = patientChartPanels(rbac);
  // INSTANT renders (discharge, treatment-sheet, invoice and visit-note stamps)
  // go through the CLINIC zone, never the browser's — DATE-KINDS.md §0.
  const formatInstant = useFormatInstant();
  // ...and so does the Coggins summary's "today" (§2.9).
  const clinicTz = useClinicTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [anchorPos, setAnchorPos] = useState<{ top: number; left: number } | null>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [alertsDialogOpen, setAlertsDialogOpen] = useState(false);
  const [editPatientOpen, setEditPatientOpen] = useState(false);
  const [showDischargeDialog, setShowDischargeDialog] = useState(false);
  // Which discharge summary to open the dialog on (null = list/create view).
  // Set when a discharge row is clicked so the draft opens directly.
  const [dischargeInitialId, setDischargeInitialId] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  // The header's vaccination counts open the Vaccinations tab, which sits below the fold.
  const recordsRef = useRef<HTMLDivElement>(null);
  const [soapDialogOpen, setSoapDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedSoapNote, setSelectedSoapNote] = useState<SoapNote | null>(null);
  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false);
  // Opens the LogRefillRequestDialog with this horse preselected — staff
  // uses this when an owner calls or walks in asking for a refill.
  const [logRefillOpen, setLogRefillOpen] = useState(false);
  const [procedureDialogOpen, setProcedureDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<any>(null);
  const [vaccinationDialogOpen, setVaccinationDialogOpen] = useState(false);
  const [editingVaccination, setEditingVaccination] = useState<any | null>(null);
  const [clientCommDialogOpen, setClientCommDialogOpen] = useState(false);
  const [visitNoteDialogOpen, setVisitNoteDialogOpen] = useState(false);
  const [editingVisitNote, setEditingVisitNote] = useState<MedicalRecord | null>(null);
  const [clinicalSearchTerm, setClinicalSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);

  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceNotification, setInvoiceNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error';
    invoiceId?: string;
  }>({ open: false, message: '', type: 'success' });
  const [confirmAction, setConfirmAction] = useState<{ message: string; onConfirm: () => void } | null>(null);

  // Check permissions
  // The permission arms used to name slugs that were in NO vocabulary
  // (`manage:horses`, `delete:medical-records`) and so only ever resolved through
  // `manage:all`. Re-pointed by the P3 sweep to the slugs the endpoints actually
  // require: POST/PUT/DELETE /horses → manage:patients, the record DELETEs →
  // manage:medical_records_archive.
  //
  // ⚠ P6 DELETED the role arms on the two flags below (ADR-0035 A4). They used to be
  // `hasRole(...) || hasPermission(...)`, with a role arm that admitted every legacy
  // role on its own — so the permission arm could never bind, and a per-user revoke
  // through the permissions editor left the Edit button and the billing panel on
  // screen while the request 403'd. DELETED, not ANDed: a CSR holds manage:patients
  // and manage:charges and the API admits it to POST/PUT/DELETE /horses, so the
  // conjunction would have taken the front desk's chart edits away — P5's grant
  // reversed by the very change meant to honour it.
  //
  // What the role arm used to buy is now bought where it belongs, at the CONTROLS.
  // hasPermission answers false for the whole in-flight window of the /users/me read
  // (useRBAC.ts:269-274 serves NO_PERMISSIONS until the shared store answers for this
  // uid), so the chart's primary actions render MOUNTED and DISABLED while
  // permissionsPending is true rather than blinking out and back on every reload —
  // ChatPanel's idiom (ChatPanel.tsx:413-422), without its text. Once the read has
  // ANSWERED or FAILED both flags fail CLOSED, and a failure is explained by the
  // shell's PermissionsUnavailableBanner, never by this screen. Pinned in
  // auth/useRBAC.test.tsx and in backend/src/auth/permissions-docs.spec.ts.
  const canEdit = can.editPatient;
  // THE CLINICAL ADDS ARE A DIFFERENT CAPABILITY FROM THE PATIENT EDITS (P5, Ali default #64).
  // `canEdit` is `manage:patients` — the slug POST/PUT/DELETE /horses requires — and it gates the
  // Edit-patient button and the record kebabs correctly. It used to gate the four ADD blocks too
  // (visit note, medication, procedure, vaccination), and those POST to /medical-records,
  // /medications, /procedures and /vaccinations, every one of which requires
  // `manage:medical_records`. A CSR holds `manage:patients` and NOT `manage:medical_records`, so
  // the front desk saw four buttons whose dialogs 403 on save — the "control that appears to work
  // and does not" shape this program exists to end. Gated on the slug the ENDPOINT requires
  // instead. No legacy role loses anything: all four hold `manage:medical_records`.
  // ⚠ Permission-only, with no role arm, ON PURPOSE — an ORed role arm would re-admit every
  // clinical role's buttons for a CSR only if the role list grew, but it would also defeat a P4
  // revoke ("this tech may not edit charts"), which is the whole point of the editor. The cost is
  // the same one `canArchive` already pays: the buttons wait for the /users/me read instead of
  // rendering on the role alone. Pinned by useRBAC.test.tsx.
  const canAddClinical = can.addClinicalRecord;
  // The slug the thirteen archive routes require, and nothing else (ADR-0035 A10, as on the
  // iPhone). Pinned by useRBAC.test.tsx ("both HorseProfile screens gate canArchive…").
  const canArchive = can.archive;
  // ⚠ WAS A ROLE QUARTET while POST /soap-notes declares manage:medical_records
  // (soap-notes.controller.ts:44) — the first of the three defects the product owner found on
  // staging. The full note lives on HorseProfileModern; both layouts ask the map now.
  const canCreateSoapNote = can.newSoapNote;
  // Permission-ONLY since P6, for the reason spelled out above the canEdit line. Two
  // consumers on this layout: the Invoices tab's New-Invoice button and the
  // PatientHeaderCard `showBilling` chips.
  const canManageBilling = can.newInvoice;

  // Fetch horse data
  const { data: horse, isLoading: horseLoading, isError: horseError } = useQuery<Horse>(
    ['horse', horseId],
    withOfflineFallback(
      () => horseApi.get(horseId!),
      () => readHorse(horseId!) as any,
    ),
    { enabled: !!horseId }
  );

  // Push to the Patients-page "Recently viewed" rail when the horse loads.
  useEffect(() => {
    if (!horse) return;
    markPatientViewed(user?.uid, {
      id: horse.id,
      name: horse.name,
      patientId: (horse as any).patientId,
      breed: formatBreed(horse),
      ownerName: horse.owners?.[0]?.name,
      status: horse.status,
    });
  }, [horse?.id]);

  // Fetch logged-in user's profile
  const { data: profile } = useQuery(['my-profile'], () => api.get('/users/me').then(res => res.data));

  // Toggle include archived
  const [showArchived, setShowArchived] = useState(false);

  // All per-horse queries below share `staleTime: 60_000` so switching back
  // to a horse you viewed in the last minute loads instantly from cache
  // instead of re-firing the whole fetch storm.
  const PER_HORSE_STALE_TIME = 60_000;

  // Fetch medical records (always fetch all, filter client-side)
  const { data: allRecords = [], isLoading: recordsLoading } = useQuery<MedicalRecord[]>(
    ['horse-records', horseId],
    withOfflineFallback(
      () => horseApi.getMedicalRecords(horseId!, true),
      () => readByHorse('medicalRecords', horseId!) as any,
    ),
    // GET /medical-records -> view:medical_records. The gate reaches the QUERY, not only the
    // markup: a refused read is an answer (INV-108 (a)) and this chart fires sixteen on open.
    { enabled: !!horseId && panels.medicalRecords, staleTime: PER_HORSE_STALE_TIME },
  );

  // Filter medical records based on showArchived
  const records = useMemo(() => {
    return showArchived 
      ? allRecords.filter(rec => rec.isArchived)
      : allRecords.filter(rec => !rec.isArchived);
  }, [allRecords, showArchived]);

  // Fetch attachments linked to medical records for this horse (for download buttons)
  const { data: recordAttachments = [] } = useQuery<any[]>(
    ['record-attachments', horseId],
    () => api.get('/attachments', { params: { horseId, parentType: 'medical_record' } }).then(r => r.data),
    // GET /attachments -> view:medical_records (attachments.controller.ts:60) = the Files panel.
    { enabled: !!horseId && skipWhenOffline() && panels.files, staleTime: PER_HORSE_STALE_TIME },
  );

  // All attachments for this horse — drives the "Files" tab badge count.
  // (The Files tab re-uses the same query key, so this never double-fetches.)
  const { attachments: patientFiles } = usePatientFiles(horseId, { canRead: panels.files });
  const filesCount = patientFiles.length;
  // soapNoteId -> number of attached files, for the paperclip chip on collapsed cards.
  const soapAttachmentCounts = useMemo(
    () => countSoapAttachmentsByNote(patientFiles as any[]),
    [patientFiles],
  );

  // Map parentId (medical record id) → attachment for O(1) lookup in RecordCard
  const recordAttachmentMap = useMemo(() => {
    const map: Record<string, any> = {};
    recordAttachments.forEach((a: any) => { if (a.parentId) map[a.parentId] = a; });
    return map;
  }, [recordAttachments]);

  // Fetch SOAP notes for this horse (always fetch all, filter client-side)
  const { data: allSoapNotes = [], isLoading: soapNotesLoading } = useQuery<SoapNote[]>(
    ['soap-notes', horseId],
    withOfflineFallback(
      () => soapNotesService.findAll({ horseId: horseId! }),
      () => readByHorse('soapNotes', horseId!) as any,
    ),
    { enabled: !!horseId && panels.medicalRecords, staleTime: PER_HORSE_STALE_TIME },
  );

  // Filter SOAP notes based on showArchived
  const soapNotes = useMemo(() => {
    return showArchived
      ? allSoapNotes.filter((note: any) => note.isArchived)
      : allSoapNotes.filter((note: any) => !note.isArchived);
  }, [allSoapNotes, showArchived]);

  // Fetch procedures for this horse (always fetch all, filter client-side)
  const { data: procedures = [], isLoading: proceduresLoading } = useQuery(
    ['procedures', horseId],
    withOfflineFallback(
      () => ProcedureService.getAll({ horseId: horseId!, includeArchived: true }),
      () => readByHorse('procedures', horseId!) as any,
    ),
    { enabled: !!horseId && panels.procedures, staleTime: PER_HORSE_STALE_TIME },
  );

  // Fetch medications for this horse (always fetch all, filter client-side)
  const { data: medications = [], isLoading: medicationsLoading } = useQuery(
    ['medications', horseId],
    withOfflineFallback(
      () => medicationService.getAll({ horseId: horseId!, includeArchived: true }),
      () => readByHorse('medications', horseId!) as any,
    ),
    { enabled: !!horseId && panels.medications, staleTime: PER_HORSE_STALE_TIME },
  );

  // Total non-archived medications on this horse. Passed to MedicationCard so
  // the delete button can be disabled when this drops to 1 — preserving at
  // least one medication record on the horse for audit/history.
  const totalActiveMedications = useMemo(
    () => (medications as any[]).filter((m: any) => !m.isArchived).length,
    [medications],
  );

  // Fetch vaccinations for this horse
  const { data: allVaccinations = [] } = useQuery(
    ['vaccinations', horseId],
    withOfflineFallback(
      () => VaccinationService.getAll({ horseId: horseId!, includeArchived: true }),
      () => readByHorse('vaccinations', horseId!) as any,
    ),
    { enabled: !!horseId && panels.vaccinations, staleTime: PER_HORSE_STALE_TIME },
  );

  // Fetch low-stock inventory items
  // Low-stock inventory flags used to highlight medication/vaccination cards.
  // This used to `refetchInterval: 60000` (poll every minute) which was
  // pointless on a detail page — inventory doesn't change that often and the
  // user isn't watching for real-time updates here. Now relies on the
  // global 30s staleTime + 5min cacheTime.
  const { data: lowStockItems = [] } = useQuery(
    ['low-stock-inventory'],
    () => inventoryService.getLowStockItems(),
    // GET /inventory/low-stock -> view:inventory. Not a panel: without it a med/vax card loses
    // a low-stock tint rather than a section, and no chart open 403s.
    { enabled: skipWhenOffline() && can.lowStockHighlight, staleTime: 5 * 60_000 },
  );

  // Fetch client communications for this horse
  const { data: clientCommunications = [] } = useQuery<ClientCommunication[]>(
    ['client-communications', horseId],
    withOfflineFallback(
      () => clientCommunicationService.getByHorseId(horseId!),
      () => readByHorse('clientCommunications', horseId!) as any,
    ),
    { enabled: !!horseId && panels.communications, staleTime: PER_HORSE_STALE_TIME },
  );

  // What has been sent from this chart with StableDocs (timeline entries).
  const { data: stableDocsShares = [] } = useStableDocsShares(
    horseId,
    panels.medicalRecords && can.shareRecords && !permissionsPending,
  );

  // Fetch discharge summaries for this horse
  const { data: dischargeSummaries = [] } = useQuery(
    ['discharge-summaries', horseId],
    withOfflineFallback(
      () => DischargeSummaryService.getByHorseId(horseId!),
      () => readByHorse('dischargeSummaries', horseId!) as any,
    ),
    { enabled: !!horseId && panels.dischargeSummaries, staleTime: PER_HORSE_STALE_TIME },
  );

  // Fetch this horse's treatment sheets (hospitalization history). Loaded eagerly
  // so the tab count is accurate. Online-first (not replicated offline yet).
  const { data: horseTreatmentSheets = [] } = useQuery(
    ['horse-treatment-sheets', horseId],
    () => treatmentSheetApi.getHorseSheets(horseId!),
    { enabled: !!horseId && panels.treatmentSheets, staleTime: PER_HORSE_STALE_TIME },
  );

  // Fetch invoice stats for this horse
  const { data: horseInvoiceStats } = useQuery<InvoiceStats>(
    ['horse-invoice-stats', horseId],
    withOfflineFallback(
      () => billingApi.getHorseStats(horseId!),
      () => readHorseInvoiceStats(horseId!) as Promise<InvoiceStats>,
    ),
    // GET /billing/stats/horse/:horseId -> view:billing (billing.controller.ts:909). Ungated
    // until now, so a person without the money slug 403'd on every chart open — and the figure
    // it feeds is the Balance chip on PatientHeaderCard, which is exactly the number they may
    // not see. `!permissionsPending` for the same reason as the list below: do not ask for a
    // client's balance on behalf of somebody the read may be about to refuse.
    { enabled: !!horseId && !permissionsPending && panels.invoices }
  );

  // Fetch invoice list only when the Invoices tab is active.
  const { data: horseInvoiceList } = useQuery<any>(
    ['horse-invoices', horseId],
    withOfflineFallback(
      () => billingApi.listInvoicesPaginated({ page: 1, limit: 50, horseId: horseId! }),
      () => readInvoicesPageByHorse(horseId!) as any,
    ),
    // GET /billing -> view:billing (billing.controller.ts:138).
    { enabled: !!horseId && tabValue === 8 && !permissionsPending && panels.invoices, staleTime: PER_HORSE_STALE_TIME }
  );

  // Fetch appointments for THIS horse to determine last visit. Previously
  // this pulled every appointment in the clinic — a serious perf bug on
  // large tenants. Now scoped to the horse via a query param.
  const { data: horseAppointmentsForLastVisit = [] } = useQuery(
    ['horse-appointments', horseId],
    withOfflineFallback(
      () => appointmentApi.list({ horseId: horseId! }),
      () => readByHorse('appointments', horseId!) as any,
    ),
    // GET /appointments -> view:appointments. A header stamp, so a blank field, not a sentence.
    { enabled: !!horseId && can.lastVisit, staleTime: 30_000 },
  );

  // Imaging studies fetch moved into LabsDiagnosticsPanel — it now owns
  // the unified "pending requests + completed studies + lab results" view.

  const clinicId = claims?.clinicId;

  const { data: clinicSettings } = useQuery(
    ['clinic-settings', clinicId],
    async () => {
      const response = await api.get(`/clinics/${clinicId}`);
      return {
        showAllMedications: response.data.showAllMedications ?? true,
        showAllVaccinations: response.data.showAllVaccinations ?? true,
        showAllProcedures: response.data.showAllProcedures ?? true,
      };
    },
    { enabled: !!clinicId, staleTime: 5 * 60 * 1000 }
  );

  // serviceCatalog is a replicated entity and serviceCatalogService.getAll is
  // offline-aware internally (withOfflineFallback → readServiceCatalog), so the
  // three catalog queries stay enabled offline — gating them would open the
  // med/vax/procedure dialogs with an empty priced group, and a med picked
  // without its catalog price syncs with unitPrice undefined (silent $0 bill).
  const { data: medicationCatalog = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'medication'],
    () => serviceCatalogService.getAll({ category: 'medication' }),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: vaccinationCatalog = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'vaccination'],
    () => serviceCatalogService.getAll({ category: 'vaccination' }),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: procedureCatalog = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'procedure'],
    () => serviceCatalogService.getAll({ category: 'procedure' }),
    { staleTime: 5 * 60 * 1000 }
  );

  // Tab badge counts — share react-query keys with the panels so no duplicate fetches.
  const { data: labResultsForCount = [] } = useQuery<any[]>(
    ['lab-results', horseId],
    withOfflineFallback(
      () => api.get('/lab-results', { params: { horseId } }).then(r => r.data),
      () => readActiveByHorse('labResults', horseId!) as any,
    ),
    { enabled: !!horseId && panels.labs },
  );
  // Diagnostic-records leg of the Labs badge = medical-record rows of these types,
  // which ARE replicated — recreate the same filter offline (mirrors Modern).
  const DIAG_RECORD_TYPES = ['diagnostic', 'imaging', 'outside_lab'];
  const { data: diagRecordsForCount = [] } = useQuery<any[]>(
    ['med-records-diag', horseId],
    withOfflineFallback(
      () => api.get('/medical-records', { params: { horseId, types: 'diagnostic,imaging,outside_lab' } }).then(r => r.data),
      async () => {
        const rows = await readByHorse('medicalRecords', horseId!);
        return rows.filter((r: any) => DIAG_RECORD_TYPES.includes(r.type)) as any;
      },
    ),
    { enabled: !!horseId && panels.labs },
  );
  // Imaging studies + open imaging requests roll up into the Labs &
  // Diagnostics tab badge so the count matches what's actually visible inside
  // the panel (one unified Pending + Results timeline).
  const { data: keystoneStudiesForCount = [] } = useQuery<any[]>(
    ['horse-keystone-studies', horseId],
    () => api.get(`/keystone/horse/${horseId}/studies`).then(r => r.data),
    // keystone.controller.ts getStudiesForHorse :1035 -> view:medical_records, same as the rest
    // of the Labs feeds. The integration does not make it a stricter gate.
    { enabled: !!horseId && skipWhenOffline() && panels.labs },
  );
  const { data: imagingRequestsForCount = [] } = useQuery<any[]>(
    ['imaging-requests', horseId],
    () => api.get('/keystone/imaging-requests', { params: { horseId } }).then(r => r.data),
    { enabled: !!horseId && skipWhenOffline() && panels.labs },
  );
  const { data: problemsForCount = [] } = useQuery<any[]>(
    ['problem-list', horseId],
    withOfflineFallback(
      () => api.get('/problem-list', { params: { horseId } }).then(r => r.data),
      () => readActiveByHorse('problemLists', horseId!) as any,
    ),
    { enabled: !!horseId && panels.problems },
  );
  const { data: recallsForCount = [] } = useQuery<any[]>(
    ['recall-reminders', horseId],
    withOfflineFallback(
      () => api.get('/recall-reminders', { params: { horseId } }).then(r => r.data),
      () => readByHorse('recallReminders', horseId!) as any,
    ),
    { enabled: !!horseId && panels.recalls },
  );
  // Open imaging requests = DRAFT or SUBMITTED (not yet finalized).
  // Cancelled drafts don't contribute to the count.
  const openImagingRequestsCount = imagingRequestsForCount.filter(
    (r: any) => r.submissionStatus === 'DRAFT' || r.submissionStatus === 'SUBMITTED',
  ).length;
  const labsCount =
    labResultsForCount.length +
    diagRecordsForCount.length +
    keystoneStudiesForCount.length +
    openImagingRequestsCount;

  // Archive record mutation (soft delete)
  const archiveRecord = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'medicalRecord',
        table: 'medicalRecords',
        entityId: id,
        payload: { isArchived: true, archivedAt: new Date().toISOString() },
        networkFn: () => medicalRecordsApi.archive(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['horse-records', horseId]);
        setAnchorPos(null);
        enqueueSnackbar('Record archived', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to archive record', { variant: 'error' });
      },
    }
  );

  const restoreRecord = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'medicalRecord',
        table: 'medicalRecords',
        entityId: id,
        payload: { isArchived: false, archivedAt: null },
        networkFn: () => medicalRecordsApi.restore(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['horse-records', horseId]);
        enqueueSnackbar('Record restored', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to restore record', { variant: 'error' });
      },
    }
  );

  const createClientCommunication = useMutation(
    (data: Omit<ClientCommunication, 'id'>) =>
      executeWrite<ClientCommunication>({
        entityType: 'clientCommunication',
        table: 'clientCommunications',
        payload: data,
        networkFn: () => clientCommunicationService.create(data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-communications', horseId]);
        enqueueSnackbar('Communication recorded', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to save communication', { variant: 'error' });
      },
    }
  );

  const createVisitNote = useMutation(
    (data: { horseId: string; type: string; date: string; notes: string; tags: string[] }) =>
      executeWrite({
        entityType: 'medicalRecord',
        table: 'medicalRecords',
        payload: data,
        networkFn: () => medicalRecordsApi.create(data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['horse-records', horseId]);
        enqueueSnackbar('Visit note saved', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to save visit note', { variant: 'error' });
      },
    }
  );

  const updateVisitNote = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      executeUpdate({
        entityType: 'medicalRecord',
        table: 'medicalRecords',
        entityId: id,
        payload: data,
        networkFn: () => medicalRecordsApi.update(id, data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['horse-records', horseId]);
        enqueueSnackbar('Visit note updated', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to update visit note', { variant: 'error' });
      },
    }
  );

  const handleVisitNoteSave = (data: { horseId: string; type: string; date: string; notes: string; tags: string[] }) => {
    if (editingVisitNote) {
      updateVisitNote.mutate({ id: editingVisitNote.id, data: { notes: data.notes, date: data.date, tags: data.tags } });
    } else {
      createVisitNote.mutate(data);
    }
    setVisitNoteDialogOpen(false);
    setEditingVisitNote(null);
  };

  const handleEditVisitNote = (record: MedicalRecord) => {
    setEditingVisitNote(record);
    setVisitNoteDialogOpen(true);
  };

  const deleteClientCommunication = useMutation(
    (id: string) =>
      executeDelete({
        entityType: 'clientCommunication',
        table: 'clientCommunications',
        entityId: id,
        networkFn: () => clientCommunicationService.delete(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-communications', horseId]);
        enqueueSnackbar('Communication deleted', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to delete communication', { variant: 'error' });
      },
    }
  );

  const archiveSoapNote = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'soapNote',
        table: 'soapNotes',
        entityId: id,
        payload: { isArchived: true },
        networkFn: () => soapNotesService.archive(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['soap-notes', horseId]);
        setAnchorPos(null);
        enqueueSnackbar('SOAP note archived', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to archive SOAP note', { variant: 'error' });
      },
    }
  );

  const archiveProcedure = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'procedure',
        table: 'procedures',
        entityId: id,
        payload: { isArchived: true },
        networkFn: () => ProcedureService.archive(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['procedures', horseId]);
        setAnchorPos(null);
        enqueueSnackbar('Procedure archived', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to archive procedure', { variant: 'error' });
      },
    }
  );

  const archiveVaccination = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'vaccination',
        table: 'vaccinations',
        entityId: id,
        payload: { isArchived: true },
        networkFn: () => VaccinationService.archive(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vaccinations', horseId]);
        queryClient.invalidateQueries(['horse-records', horseId]);
        setAnchorPos(null);
        enqueueSnackbar('Vaccination archived', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to archive vaccination', { variant: 'error' });
      },
    }
  );

  // Sort records chronologically (most recent first for better UX)
  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [records]);

  // Filter and sort procedures based on showArchived
  const filteredProceduresForAll = useMemo(() => {
    const filtered = showArchived 
      ? procedures.filter(proc => proc.isArchived) 
      : procedures.filter(proc => !proc.isArchived);
    // Sort by date descending (most recent first)
    return [...filtered].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [procedures, showArchived]);

  // Filter and sort medications based on showArchived
  const filteredMedicationsForAll = useMemo(() => {
    const filtered = showArchived 
      ? medications.filter(med => med.isArchived) 
      : medications.filter(med => !med.isArchived);
    // Sort by startDate descending (most recent first)
    return [...filtered].sort((a, b) => 
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  }, [medications, showArchived]);

  // Filter vaccinations based on showArchived
  const filteredVaccinations = useMemo(() => {
    const filtered = showArchived
      ? allVaccinations.filter((v: any) => v.isArchived)
      : allVaccinations.filter((v: any) => !v.isArchived);
    return [...filtered].sort((a: any, b: any) =>
      new Date(b.dateAdministered).getTime() - new Date(a.dateAdministered).getTime()
    );
  }, [allVaccinations, showArchived]);

  // Group ALL medications by CALENDAR day for counting (includes archived).
  // The day key is the date PART of Medication.startDate (chartDates.ts) — the
  // old browser-zone key filed a UTC-midnight row under the previous day.
  const allGroupedMedications = useMemo(
    () => groupMedicationsByStartDay(medications as any[]),
    [medications],
  );

  // Group medications by CALENDAR day for display (filtered)
  const groupedMedications = useMemo(
    () => groupMedicationsByStartDay(filteredMedicationsForAll as any[]),
    [filteredMedicationsForAll],
  );

  // Combine and sort all clinical data
  const allClinicalData = useMemo(() => {
    const combined = [
      ...sortedRecords.filter(r => r.type !== 'medication' && r.type !== 'vaccination').map(record => ({ ...record, itemType: 'record' })),
      ...soapNotes.map(note => ({ ...note, itemType: 'soap', date: note.date })),
      ...filteredProceduresForAll.map(proc => ({ 
        ...proc, 
        itemType: 'procedure', 
        date: proc.date,
        type: 'procedure',
        id: proc.id
      })),
      ...groupedMedications.map(group => ({
        ...group,
        itemType: 'medication-group',
        date: group.date,
        id: `medication-group-${group.date}`,
      })),
      ...filteredVaccinations.map((v: any) => ({
        ...v,
        itemType: 'vaccination',
        type: 'vaccination',
        date: v.dateAdministered,
      })),
      ...stableDocsShares.map((s) => ({ ...s, itemType: 'stabledocs', date: s.sentAt })),
    ];
    
    return combined.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [sortedRecords, soapNotes, filteredProceduresForAll, groupedMedications, filteredVaccinations, stableDocsShares]);

  // Group records by type for the tabbed view
  const recordsByType = useMemo(() => {
    return {
      all: allClinicalData,
      visitNote: sortedRecords.filter(r => r.type === 'visit_note' || r.type === 'Visit Note').map(r => ({ ...r, itemType: 'record' })),
      soap: soapNotes,
      medication: groupedMedications,
      procedure: filteredProceduresForAll.map(proc => ({
        ...proc,
        itemType: 'procedure',
        type: 'procedure',
        date: proc.date
      })),
      vaccination: filteredVaccinations.map((v: any) => ({
        ...v,
        itemType: 'vaccination',
        type: 'vaccination',
        date: v.dateAdministered,
      })),
    };
  }, [soapNotes, filteredProceduresForAll, groupedMedications, filteredVaccinations, allClinicalData, sortedRecords]);

  // Compute tag suggestions from existing visit notes (sorted by frequency, min 3 uses)
  const visitNoteTagSuggestions = useMemo(() => {
    const tagCounts = new Map<string, number>();
    records
      .filter(r => r.type === 'visit_note' && r.tags)
      .forEach(r => (r.tags as string[]).forEach(t => tagCounts.set(t, (tagCounts.get(t) || 0) + 1)));
    return Array.from(tagCounts.entries())
      .filter(([tag, count]) => tag !== 'Imported' && count >= 3)
      .sort((a, b) => b[1] - a[1])
      .map(([tag]) => tag);
  }, [records]);

  // Search, date range, and tag filter for clinical records
  const searchFilteredRecords = useMemo(() => {
    const term = clinicalSearchTerm.toLowerCase().trim();
    const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
    const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    const hasFilters = term || fromDate || toDate || selectedTagFilter;
    if (!hasFilters) return recordsByType;

    const matchesDate = (item: any): boolean => {
      if (!fromDate && !toDate) return true;
      const itemDate = new Date(item.date || item.dateAdministered || item.startDate);
      if (fromDate && itemDate < fromDate) return false;
      if (toDate && itemDate > toDate) return false;
      return true;
    };

    const matchesTag = (item: any): boolean => {
      if (!selectedTagFilter) return true;
      const tags = item.tags as string[] | undefined;
      return tags ? tags.includes(selectedTagFilter) : false;
    };

    const matchesSearch = (item: any): boolean => {
      if (!term) return true;
      const fields = [
        item.notes, item.name, item.description, item.findings,
        item.vaccineName, item.instructions, item.type,
        item.subjective?.text || item.subjective?.notes || item.subjective?.aiSummary,
        item.objective?.text || item.objective?.notes || item.objective?.aiSummary,
        item.assessment?.text || item.assessment?.notes || item.assessment?.primaryDiagnosis,
        item.plan?.text || item.plan?.planNotes,
        item.templateUsed, item.recipientName, item.recipientEmail,
        // Also search tags
        ...(Array.isArray(item.tags) ? item.tags : []),
      ];
      return fields.some(f => f && String(f).toLowerCase().includes(term));
    };

    const matchesMedGroup = (group: any): boolean => {
      if (!matchesDate(group)) return false;
      if (!term) return true;
      if (!group.medications) return false;
      return group.medications.some((m: any) =>
        [m.name, m.instructions, m.dosage, m.notes].some(f => f && String(f).toLowerCase().includes(term))
      );
    };

    const filterItem = (item: any): boolean => {
      if (!matchesDate(item)) return false;
      if (!matchesTag(item)) return false;
      if (item.itemType === 'medication-group') return matchesMedGroup(item);
      return matchesSearch(item);
    };

    return {
      all: recordsByType.all.filter(filterItem),
      visitNote: recordsByType.visitNote.filter(item => matchesDate(item) && matchesTag(item) && matchesSearch(item)),
      soap: recordsByType.soap.filter(item => matchesDate(item) && matchesSearch(item)),
      medication: recordsByType.medication.filter(matchesMedGroup),
      procedure: recordsByType.procedure.filter(item => matchesDate(item) && matchesSearch(item)),
      vaccination: recordsByType.vaccination.filter(item => matchesDate(item) && matchesSearch(item)),
    };
  }, [recordsByType, clinicalSearchTerm, dateFrom, dateTo, selectedTagFilter]);

  // Get last visit date from past appointments
  const lastVisitDate = useMemo(() => {
    if (!horseId) return null;

    // The query is already horseId-scoped; just pick the most recent past
    // appointment.
    const pastAppointments = horseAppointmentsForLastVisit
      .filter((apt: any) => new Date(apt.date) < new Date())
      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

    if (pastAppointments.length === 0) return null;

    return new Date(pastAppointments[0].date);
  }, [horseAppointmentsForLastVisit, horseId]);

  /**
   * The horse's CURRENT Coggins, whichever source recorded it last.
   *
   * Coggins is written two ways in this product and the header has to read
   * BOTH, or it denies paperwork the clinic is holding:
   *
   *   LEGACY   a `Vaccination` whose `vaccineName` says Coggins (ADR-0019 D4).
   *            Carries a `nextDueDate` and nothing else - no number, no
   *            certificate. Untouched by this change and still fully supported.
   *   NEW      a `LabResult` with an `expiryDate` (INV-102). The only one of the
   *            two that can express the accession NUMBER and the certificate,
   *            which is why the manager asked for it.
   *
   * NEWEST WINS, not "prefer lab results": a horse has exactly ONE current
   * Coggins, and the honest answer is the most recent EVENT. Ranking by source
   * would let a 2020 lab result outrank a 2026 vaccination-path record. The
   * comparison key is the event date (`reportDate` / `dateAdministered`), never
   * the expiry - a shorter-dated newer test still supersedes an older one. This
   * mirrors the backend's cross-source `hasNewerCogginsEvent` gate, which is
   * what stops the reminder generator emailing on a back-entered historical row.
   *
   * `labResultsForCount` is ALREADY fetched above (react-query key
   * `['lab-results', horseId]`, shared with the Labs panel, with an offline
   * replica fallback), so this costs no request and works offline.
   *
   * ⚠ The name test is `isCogginsName`, the SAME literal the backend classifies
   * with (`utils/coggins` is its declared twin), not the inline `/coggins|eia/i`
   * that used to live here. That old regex had no word boundaries, so a vaccine
   * named "Media"-anything would have matched; more importantly, two copies of
   * the rule is how the chart and a client-facing email end up disagreeing.
   */
  const coggins = useMemo<{
    expiry: string | null;
    accession: string | null;
    attachmentId: string | null;
    positive: boolean;
  } | null>(() => {
    type Candidate = { when: string; expiry: string | null; accession: string | null; attachmentId: string | null; positive: boolean };
    const candidates: Candidate[] = [];

    for (const v of allVaccinations as any[]) {
      if (v.isArchived || !isCogginsName(v.vaccineName)) continue;
      candidates.push({
        when: v.dateAdministered,
        expiry: v.nextDueDate ?? null,
        accession: null,
        attachmentId: null,
        positive: false,
      });
    }
    for (const lr of labResultsForCount as any[]) {
      // A lab result in TRIAGE has no horse yet; the query is horse-scoped, but
      // the offline replica fallback is not necessarily, so re-check.
      if (lr.horseId !== horseId) continue;
      if (!isCogginsLabResultRow(lr)) continue;
      candidates.push({
        when: lr.reportDate,
        expiry: lr.expiryDate ?? null,
        accession: lr.accessionNumber ?? null,
        attachmentId: lr.pdfAttachmentId ?? null,
        positive: isPositiveCogginsResult(lr.results),
      });
    }
    if (candidates.length === 0) return null;

    // Newest record for identity; newest STILL-VALID known expiry for status
    // (pickCogginsSummary, chartDates.ts) - a NULL-expiry IDEXX result must not
    // hide a dated vaccination's green chip.
    const picked = pickCogginsSummary(candidates, clinicTz);
    if (!picked) return null;
    const { expiry, accession, attachmentId, positive } = picked;
    return { expiry, accession, attachmentId, positive };
  }, [allVaccinations, labResultsForCount, horseId, clinicTz]);

  // Excludes Coggins with the same classifier as the memo above, so no vaccine lands in both badges (or neither).
  // The header counts and the Vaccinations tab read the same standings (chartDates).
  const vaccinationSummary = useMemo(() => summarizeVaccinations(allVaccinations as any[], clinicTz), [allVaccinations, clinicTz]);

  const recordTypeLabels: Record<string, string> = {
    diagnostic: 'Diagnostic',
    imaging: 'Imaging',
    outside_lab: 'Outside Lab',
    exam: 'Clinical Exam',
    note: 'General Note',
    visit_note: 'Visit Note',
    'Visit Note': 'Visit Note',
  };

  const getRecordIcon = (type: string) => {
    if (type === 'diagnostic' || type === 'imaging' || type === 'outside_lab') return <ScienceIcon />;
    if (type === 'exam') return <AssignmentIcon />;
    if (type === 'visit_note' || type === 'Visit Note') return <NoteIcon />;
    return <NoteIcon />;
  };

  const handleAddEntry = (type: string) => {
    if (type === 'soap') {
      setSelectedSoapNote(null);
      setSoapDialogOpen(true);
    } else if (type === 'visit-note') {
      setEditingVisitNote(null);
      setVisitNoteDialogOpen(true);
    } else if (type === 'medication') {
      setMedicationDialogOpen(true);
    } else if (type === 'procedure') {
      setProcedureDialogOpen(true);
    } else if (type === 'vaccination') {
      setVaccinationDialogOpen(true);
    }
  };

  const handleSoapNoteSave = (note: SoapNote) => {
    // Refresh the medical records list
    queryClient.invalidateQueries(['horse-records', horseId]);
    queryClient.invalidateQueries(['soap-notes', horseId]);
    queryClient.invalidateQueries(['medications', horseId]);
    queryClient.invalidateQueries(['low-stock-inventory']);
    setSoapDialogOpen(false);
    setSelectedSoapNote(null); // Clear selection
  };

  const handleViewSoapNote = (note: SoapNote) => {
    setSelectedSoapNote(note);
    setSoapDialogOpen(true);
  };

  const handleEditSoapNote = (note: SoapNote) => {
    setSelectedSoapNote(note);
    setSoapDialogOpen(true);
  };

  const handleMedicationSave = async (medications: any[]) => {
      try {
      // Create medications directly using the new API, collecting the
      // resulting records so the dialog can chain Save & Print on Rx rows.
      const created = [];
      for (const medication of medications) {
        // The exact data object the create endpoint receives. Passed to BOTH
        // executeWrite.payload (for the optimistic replica row when offline)
        // and networkFn (the unchanged online POST), so the two never diverge.
        const payload = {
          horseId: horseId!,
          name: medication.name,
          dosage: medication.dosage,
          frequency: medication.frequency,
          route: medication.route,
          duration: medication.duration,
          quantity: medication.quantity,
          instructions: medication.instructions,
          prescribedBy: medication.prescribedBy || profile?.name || user?.displayName || user?.email?.split('@')[0],
          startDate: medication.startDate || new Date().toISOString(),
          endDate: medication.endDate,
          notes: medication.notes,
          // Per-unit price from the priced catalog pick — keeps the Rx
          // auto-bill line item from being silently $0.
          unitPrice: medication.unitPrice,
          // DEA controlled-substance schedule; '' (not controlled) → undefined.
          deaSchedule: medication.deaSchedule || undefined,
          // Rx fields — backend defaults expirationDate to startDate + 12mo
          // when isRx=true and expirationDate is omitted, so passing undefined
          // (rather than null) here triggers that default. C-II hard-block
          // runs on the backend.
          isRx: medication.isRx || undefined,
          refillsAuthorized: medication.isRx ? (medication.refillsAuthorized ?? 0) : undefined,
          expirationDate: medication.isRx && medication.expirationDate
            ? medication.expirationDate
            : undefined,
          // "Deduct stock from" — undefined = Automatic (server infers, ADR-0013).
          dispenseLocationId: medication.dispenseLocationId,
          // Bottle for the DEA auto-log; undefined = server FIFO pick (ADR-0014).
          deaLotId: medication.deaLotId,
        };
        const result = await executeWrite({
          entityType: 'medication',
          table: 'medications',
          payload,
          networkFn: () => medicationService.create(payload),
        });
        created.push(result);
      }

      // Refresh queries
      queryClient.invalidateQueries(['medications', horseId]);
    queryClient.invalidateQueries(['low-stock-inventory']);
    queryClient.invalidateQueries(['horse-records', horseId]);
    enqueueSnackbar('Medications saved successfully', { variant: 'success' });
    setMedicationDialogOpen(false);
    return created;
    } catch (error: any) {
      // Offline write blocked — now only on out-of-storage (quota) or an
      // online-only action (e.g. invoices), NOT persist()-denial. Surface that
      // specific message instead of the generic save-failure one.
      const isBlocked =
        error instanceof OfflineWriteBlockedError ||
        error?.name === 'OfflineWriteBlockedError';
      enqueueSnackbar(
        isBlocked
          ? error.message
          : error?.response?.data?.message || 'Failed to save medications',
        { variant: 'error' },
      );
      // Re-throw so the dialog stays open + shows the inline error too
      throw error;
    }
  };

  const handleProcedureSave = async (data: { procedure: string; template: string; specifics: string; date: Date; performedBy: string }) => {
    try {
      if (editingProcedure) {
        // Update existing procedure
        const updateData = {
          name: data.procedure,
          description: data.specifics,
          category: data.template || undefined,
          date: data.date.toISOString(),
          performedBy: data.performedBy || undefined,
          notes: data.template ? `Template: ${data.template}` : undefined,
        };
        await executeUpdate({
          entityType: 'procedure',
          table: 'procedures',
          entityId: editingProcedure.id,
          payload: updateData,
          networkFn: () => ProcedureService.update(editingProcedure.id, updateData),
        });
      } else {
        // Create new procedure
        const createData = {
          horseId: horseId!,
          name: data.procedure,
          description: data.specifics,
          category: data.template || undefined,
          date: data.date.toISOString(),
          performedBy: data.performedBy || undefined,
          notes: data.template ? `Template: ${data.template}` : undefined,
        };
        // Offline-aware create: online (or flag off) this is byte-identical to
        // ProcedureService.create; offline it queues + optimistically writes the row.
        await executeWrite({
          entityType: 'procedure',
          table: 'procedures',
          payload: createData,
          networkFn: () => ProcedureService.create(createData),
        });
      }

      // Refresh the procedures list and all clinical data
      queryClient.invalidateQueries(['procedures', horseId]);
      queryClient.invalidateQueries(['horse-records', horseId]);
      setProcedureDialogOpen(false);
      setEditingProcedure(null);
    } catch (err: any) {
      console.error(`Failed to ${editingProcedure ? 'update' : 'create'} procedure:`, err);
      if (err instanceof OfflineWriteBlockedError || err?.name === 'OfflineWriteBlockedError') {
        // Offline write blocked (out-of-storage or online-only) — surface the real reason.
        enqueueSnackbar(err.message, { variant: 'error' });
      }
      throw err; // Re-throw so the dialog can display the error
    }
  };

  const handleEditProcedure = (procedure: any) => {
    setEditingProcedure(procedure);
    setProcedureDialogOpen(true);
  };

  const handleVaccinationSave = async (vaccinations: any[]) => {
    try {
      for (const vaccination of vaccinations) {
        const data = toCreateVaccinationDto(vaccination, horseId!);
        await executeWrite({
          entityType: 'vaccination',
          table: 'vaccinations',
          payload: data,
          networkFn: () => VaccinationService.create(data),
        });
      }
      queryClient.invalidateQueries(['vaccinations', horseId]);
      queryClient.invalidateQueries(['low-stock-inventory']);
      queryClient.invalidateQueries(['horse-records', horseId]);
      enqueueSnackbar('Vaccinations saved successfully', { variant: 'success' });
      setVaccinationDialogOpen(false);
    } catch (error: any) {
      if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
        enqueueSnackbar(error.message, { variant: 'error' });
        return;
      }
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to save vaccinations',
        { variant: 'error' },
      );
    }
  };

  const handleCloseSoapDialog = () => {
    setSoapDialogOpen(false);
    setSelectedSoapNote(null); // Clear selection when closing
  };

  const handleArchiveRecord = (id: string) => {
    setConfirmAction({
      message: 'Are you sure you want to archive this record? You can restore it later if needed.',
      onConfirm: () => archiveRecord.mutate(id),
    });
  };

  const handleArchiveSoapNote = (id: string) => {
    setConfirmAction({
      message: 'Are you sure you want to archive this SOAP note? You can restore it later if needed.',
      onConfirm: () => archiveSoapNote.mutate(id),
    });
  };

  const handleArchiveProcedure = (id: string) => {
    setConfirmAction({
      message: 'Are you sure you want to archive this procedure? You can restore it later if needed.',
      onConfirm: () => archiveProcedure.mutate(id),
    });
  };

  const handleArchiveVaccination = (id: string) => {
    setConfirmAction({
      message: 'Are you sure you want to archive this vaccination? You can restore it later if needed.',
      onConfirm: () => archiveVaccination.mutate(id),
    });
  };

  const handleSoapNoteMenuOpen = (event: React.MouseEvent<HTMLElement>, soapNoteId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPos({ top: rect.bottom, left: rect.right });
    setSelectedRecordId(soapNoteId);
  };

  const handleProcedureMenuOpen = (event: React.MouseEvent<HTMLElement>, procedureId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPos({ top: rect.bottom, left: rect.right });
    setSelectedRecordId(procedureId);
  };

  const handleVaccinationMenuOpen = (event: React.MouseEvent<HTMLElement>, vaccinationId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPos({ top: rect.bottom, left: rect.right });
    setSelectedRecordId(vaccinationId);
  };

  const handleRecordMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, recordId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPos({ top: rect.bottom, left: rect.right });
    setSelectedRecordId(recordId);
  };

  const handleRecordMenuClose = () => {
    setAnchorPos(null);
    setSelectedRecordId(null);
  };

  // Get medical alerts from horse data (now structured)
  const medicalAlerts = useMemo<Array<{ id: string; text: string; isCritical: boolean }>>(() => {
    const alerts = horse?.medicalAlerts || [];
    // Handle both old format (strings) and new format (objects)
    if (Array.isArray(alerts) && alerts.length > 0) {
      if (typeof alerts[0] === 'string') {
        // Old format - convert to display format
        return alerts.map((alert: any) => ({
          id: Math.random().toString(),
          text: alert,
          isCritical: false,
        }));
      }
      return alerts as unknown as Array<{ id: string; text: string; isCritical: boolean }>;
    }
    return [];
  }, [horse]);

  const handleGenerateInvoice = () => {
    if (!horseId) return;
    // Navigate to billing form with pre-populated horse data (consistent with appointments flow)
    navigate('/billing', { state: { horseId, autoImportHorse: true } });
  };

  const handleViewInvoice = () => {
    if (invoiceNotification.invoiceId) {
      navigate('/billing', { state: { invoiceId: invoiceNotification.invoiceId } });
    }
  };

  if (horseLoading || recordsLoading || soapNotesLoading || proceduresLoading) {
    return (
      <Container maxWidth="xl">
        <Box py={3}>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={400} />
        </Box>
      </Container>
    );
  }

  if (horseError) {
    return (
      <Container maxWidth="xl">
        <Box py={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load patient data. Please try refreshing the page.
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/patients')}>
            Back to Clinical Records
          </Button>
        </Box>
      </Container>
    );
  }

  if (!horse) {
    return (
      <Container maxWidth="xl">
        <Box py={3} textAlign="center">
          <Typography variant="h5">Horse not found</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/patients')}
            sx={{ mt: 2 }}
          >
            Back to Search
          </Button>
        </Box>
      </Container>
    );
  }

  const RecordCard = ({ record }: { record: MedicalRecord }) => {
    const attachment = recordAttachmentMap[record.id];
    const [viewerOpen, setViewerOpen] = useState(false);

    // For migrated docs the notes are: "[Imported Document] Title\nSource file: filename.ext"
    // Show just the title and a download button; hide the raw "Source file:" line.
    const isImported = record.notes?.startsWith('[Imported Document]');
    const importedTitle = isImported
      ? record.notes!.split('\n')[0].replace('[Imported Document] ', '').trim()
      : null;
    const displayNotes = isImported ? importedTitle : record.notes;

    return (
      <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  {getRecordIcon(record.type)}
                  <Typography variant="h6">
                    {recordTypeLabels[record.type] || record.type}
                  </Typography>
                </Box>
                {isImported && (
                  <Chip label="Imported" size="small" variant="outlined" color="default" />
                )}
              </Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {/* MedicalRecord.date is MIXED per ROW (DATE-KINDS §1.2), and the ROW decides:
                    a visit note is an INSTANT by TYPE (both writers post a date+time, and a
                    browser's toISOString() at 8 PM EDT would otherwise LOOK like a day), and
                    every other type takes the shape rule — a picked day (T00:00Z from this
                    chart's dialog, T12:00Z from barn ops) reads as a DAY with no fabricated
                    time, an iOS-written moment reads in the clinic zone. See
                    chartDates.medicalRecordDateKind for the three writers. The old else-arm
                    was `format(new Date(record.date), 'MMMM d, yyyy h:mm a')`, which both
                    rolled the day back for a picked day AND printed it "12:00 AM" as though
                    somebody had entered a time. formatDistanceToNow diffs two instants and is
                    zone-agnostic — left as is. */}
                {medicalRecordDateKind(record.type, record.date) === 'instant'
                  ? formatInstant(record.date, INSTANT_FORMATS.dateTimeLong)
                  : formatCalendarDate(record.date, 'MMMM d, yyyy')} • {formatDistanceToNow(new Date(record.date), { addSuffix: true })}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                {displayNotes}
              </Typography>
              {record.tags && record.tags.length > 0 && (
                <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                  {record.tags.map((tag: string, i: number) => (
                    <Chip key={i} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                  ))}
                </Box>
              )}
              {attachment?.storageUrl && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    onClick={() => setViewerOpen(true)}
                    sx={{ mt: 1.5 }}
                  >
                    View Document
                  </Button>
                  <DocumentViewerModal
                    open={viewerOpen}
                    onClose={() => setViewerOpen(false)}
                    url={`/api${attachment.storageUrl}`}
                    title={importedTitle || record.notes || 'Document'}
                    mimeType={attachment.mimeType}
                  />
                </>
              )}
            </Box>
            {canEdit && (
              <IconButton
                id={`menu-button-${record.id}`}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRecordMenuOpen(e, record.id);
                }}
                aria-label="more options"
                aria-controls={selectedRecordId === record.id ? 'record-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={selectedRecordId === record.id ? 'true' : undefined}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl">
      <Box py={2}>
        {/* Navigation */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/patients')}
          sx={{ mb: 1.5 }}
          variant="outlined"
        >
          Back to Patient List
        </Button>

        {/* Unallocated result suggestions — silent unless an Inbox item
            looks like it belongs to this patient. One-click jumps to triage. */}
        {horse && panels.labs && <UnallocatedSuggestionBanner horse={horse} />}

        {/* Patient Header */}
        <Box sx={{ mb: 1.5 }}>
          <PatientHeaderCard
            horse={horse}
            stats={{
              lastVisitDate: lastVisitDate ? lastVisitDate.toISOString() : null,
              totalRecords: allRecords.length + allSoapNotes.length + procedures.length + allGroupedMedications.length + dischargeSummaries.length,
              balance: horseInvoiceStats?.balance ?? 0,
              openInvoiceCount: horseInvoiceStats?.openInvoiceCount ?? 0,
              processingAmount: horseInvoiceStats?.processingAmount ?? 0,
            }}
            cogginsExpiry={coggins?.expiry ?? null}
            cogginsHasRecord={!!coggins}
            cogginsAccession={coggins?.accession ?? null}
            cogginsPositive={coggins?.positive ?? false}
            cogginsCertificateAttachmentId={coggins?.attachmentId ?? null}
            vaccinationSummary={panels.vaccinations ? vaccinationSummary : undefined}
            onVaccinationsClick={() => {
              setTabValue(4);
              recordsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            }}
            alerts={medicalAlerts}
            onAddAlert={canEdit ? () => setAlertsDialogOpen(true) : undefined}
            showBilling={canManageBilling}
            actions={
              /* P6: MOUNTED-and-DISABLED while the permission read is in flight, absent
                 once it has ANSWERED without manage:patients. Hiding on canEdit alone
                 would blink the chart's primary control out and back on every reload. */
              canEdit || permissionsPending ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setEditPatientOpen(true)}
                  data-testid="edit-patient-button"
                  disabled={!canEdit}
                  sx={{
                    textTransform: 'none',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    '&:hover': { borderColor: 'text.secondary', bgcolor: 'transparent' },
                  }}
                >
                  Edit
                </Button>
              ) : undefined
            }
          />
        </Box>

        {/* Edit Patient — shared form dialog; saving invalidates ['horse', id]
            so the header above refreshes in place. */}
        <HorseFormDialog
          open={editPatientOpen}
          horse={horse ?? null}
          onClose={() => setEditPatientOpen(false)}
        />

        {/* Action Buttons */}
        <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} flexWrap="wrap" gap={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Clinical Records</Typography>
              <ChartLayoutToggle layout={layout} onChange={setLayout} />
            </Box>
            {/* Disabled (not hidden) while the permission read is in flight, like Edit. The PDF is
                built and emailed on the server, so it is also disabled offline (INV-19). */}
            {can.shareRecords && (
              <Tooltip title={isOffline ? 'Sharing records needs a connection' : ''}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setShareOpen(true)}
                    data-testid="share-records-btn"
                    disabled={permissionsPending || isOffline}
                    sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary', bgcolor: 'transparent' } }}
                  >
                    Share Records
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Paper>

        {/* Clinical Timeline with Tabs */}
        <Paper ref={recordsRef} sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
              '& .MuiTab-root': { minWidth: 'auto', px: 1.5 },
            }}
          >
            {/* Explicit `value` props keep each tab bound to its TabPanel index
                regardless of display order — so Treatment Sheets can sit right
                after SOAP Notes without renumbering any panel/conditional. */}
            <Tab value={0} icon={<HistoryIcon />} label={`All (${recordsByType.all.length})`} sx={{ px: 4 }} />
            <Tab value={1} icon={<DescriptionIcon />} label={`SOAP Notes (${recordsByType.soap.length})`} />
            <Tab value={12} icon={<HospitalIcon />} label={`Treatment Sheets (${(horseTreatmentSheets as any[]).length})`} />
            <Tab value={2} icon={<MedicalIcon />} label={`Medications (${recordsByType.medication.length})`} />
            <Tab value={3} icon={<HealingIcon />} label={`Procedures (${recordsByType.procedure.length})`} />
            <Tab value={4} icon={<VaccineIcon />} label={`Vaccinations (${recordsByType.vaccination.length})`} />
            <Tab value={5} icon={<NoteIcon />} label={`Visit Notes (${recordsByType.visitNote.length})`} />
            <Tab value={6} icon={<ScienceIcon />} label={`Labs & Diagnostics (${labsCount})`} />
            <Tab value={7} icon={<HospitalIcon />} label={`Discharges (${(dischargeSummaries as any[]).length})`} />
            {/* ⚠ Deliberately UNGATED, ruled in the P6 fix round rather than left silent:
                this tab and the list under it are a READ, and GET /billing +
                GET /billing/stats/horse/:horseId declare view:billing, not manage:charges.
                Gating it on the write slug would hide a list the API serves and that the
                same user opens from the Invoicing menu item, and it would make the two
                layouts of one chart disagree about one revoke. What is still owed is the
                other direction — this tab LAGS a view:billing revoke (an empty tab and a
                403 where HorseProfileModern now explains itself) and `showBilling` below
                withholds the header card's balance chips on manage:charges although the
                same figure prints inside this tab. Both are reads that belong on
                view:billing; see the blueprint rows for the follow-up. */}
            <Tab value={8} icon={<ReceiptIcon />} label={`Invoices (${horseInvoiceStats?.totalInvoiceCount ?? 0})`} />
            <Tab value={9} icon={<PhoneIcon />} label={`Client Communication (${clientCommunications.length})`} />
            <Tab value={10} icon={<WarningIcon />} label={`Problem List (${problemsForCount.length})`} />
            <Tab value={11} icon={<CalendarIcon />} label={`Recall Reminders (${recallsForCount.length})`} />
            <Tab value={13} icon={<PatientFilesIcon />} label={`Files (${filesCount})`} />
          </Tabs>

          <Divider />

          {/* Search & filter bar for clinical records */}
          {tabValue <= 5 && (
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search records..."
                value={clinicalSearchTerm}
                onChange={(e) => setClinicalSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 150, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
              />
              <TextField
                size="small"
                type="date"
                label="From"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 145, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 145, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
              />
              {(clinicalSearchTerm || dateFrom || dateTo || selectedTagFilter) && (
                <Button
                  size="small"
                  onClick={() => { setClinicalSearchTerm(''); setDateFrom(''); setDateTo(''); setSelectedTagFilter(null); }}
                  sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                >
                  Clear
                </Button>
              )}
            </Box>
          )}
          {/* Tag filter chips — show on All and Visit Notes tabs */}
          {(tabValue === 0 || tabValue === 5) && visitNoteTagSuggestions.length > 0 && (
            <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Tags:</Typography>
              {visitNoteTagSuggestions.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant={selectedTagFilter === tag ? 'filled' : 'outlined'}
                  color={selectedTagFilter === tag ? 'primary' : 'default'}
                  onClick={() => setSelectedTagFilter(selectedTagFilter === tag ? null : tag)}
                  sx={{ fontSize: '0.7rem', height: 24, cursor: 'pointer' }}
                />
              ))}
            </Box>
          )}

          <TabPanel value={tabValue} index={0}>
            {/* THE THIRD STAGING DEFECT. Every feed this tab merges is a view:medical_records
                read, so a refused person got an empty array and the arm below said "No clinical
                records yet - Start by adding a visit note or SOAP note". That is a claim about
                the PATIENT made to somebody who simply may not look. From inside the arm an
                empty list and a forbidden list are the same array, so the refusal comes first. */}
            {!panels.medicalRecords && (
              <Box data-testid="chart-no-access-timeline">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.medicalRecords.noun)} />
              </Box>
            )}
            {panels.medicalRecords && (searchFilteredRecords.all.length > 0 ? (
              searchFilteredRecords.all.map(item => {
                if (item.itemType === 'soap') {
                  return (
                    <SoapNoteCard
                      key={item.id}
                      soapNote={item}
                      onView={handleViewSoapNote}
                      onEdit={handleEditSoapNote}
                      onMenu={canArchive ? handleSoapNoteMenuOpen : undefined}
                      canEdit={can.editSoapNote}
                      attachmentCount={soapAttachmentCounts[item.id] || 0}
                    />
                  );
                } else if (item.itemType === 'stabledocs') {
                  return (
                    <Card key={item.id} sx={{ mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                      <CardContent>
                        <Typography variant="h6">StableDocs sent</Typography>
                        <ShareSentDetail share={item} />
                      </CardContent>
                    </Card>
                  );
                } else if (item.itemType === 'procedure' || item.type === 'procedure') {
                  return <ProcedureCard key={item.id} record={item} onEdit={handleEditProcedure} onMenu={canArchive ? handleProcedureMenuOpen : undefined} canEdit={can.editClinicalRecord} />;
                } else if (item.itemType === 'medication-group') {
                  // Render grouped medications just like in Medications tab
                  return <MedicationCard key={item.id} medications={item.medications} date={item.date} lowStockItems={lowStockItems.map((i: any) => i.name)} totalActiveMedications={totalActiveMedications} horseId={horseId} />;
                } else if (item.itemType === 'medication' || item.type === 'medication') {
                  return <MedicationCard key={item.id} medications={[item]} date={item.startDate} lowStockItems={lowStockItems.map((i: any) => i.name)} totalActiveMedications={totalActiveMedications} horseId={horseId} />;
                } else if (item.itemType === 'vaccination' || item.type === 'vaccination') {
                  return <VaccinationCard key={item.id} record={item} lowStockItems={lowStockItems.map((i: any) => i.name)} onMenu={canArchive ? handleVaccinationMenuOpen : undefined} attachment={recordAttachmentMap[item.id]} onEdit={setEditingVaccination} canEdit={can.editClinicalRecord} />;
                } else {
                  return <RecordCard key={item.id} record={item} />;
                }
              })
            ) : (
              <Box py={6} textAlign="center">
                <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No clinical records yet
                </Typography>
                {/* The empty state keeps its TEXT and loses its call to action. */}
                {can.addClinicalRecord && (
                  <Typography color="textSecondary" paragraph>
                    Start by adding a visit note or SOAP note
                  </Typography>
                )}
              </Box>
            ))}
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            {!panels.medicalRecords && (
              <Box data-testid="chart-no-access-tab5">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.medicalRecords.noun)} />
              </Box>
            )}
            {panels.medicalRecords && (<>
            {/* POST /medical-records requires manage:medical_records — see canAddClinical. */}
            {canAddClinical && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {searchFilteredRecords.visitNote.length} note{searchFilteredRecords.visitNote.length !== 1 ? 's' : ''}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddEntry('visit-note')}
                >
                  New Visit Note
                </Button>
              </Box>
            )}
            {searchFilteredRecords.visitNote.length > 0 ? (
              searchFilteredRecords.visitNote.map((record: any) => (
                <RecordCard key={record.id} record={record} />
              ))
            ) : (
              <Box py={4} textAlign="center">
                <Typography color="textSecondary">No visit notes recorded</Typography>
              </Box>
            )}
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* SOAP notes are a `view:medical_records` read (soap-notes.controller.ts:55), so
                this tab takes the same refusal as the All tab rather than reporting "No SOAP
                notes recorded" to somebody who may not look. */}
            {!panels.medicalRecords && (
              <Box data-testid="chart-no-access-soap">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.medicalRecords.noun)} />
              </Box>
            )}
            {panels.medicalRecords && (<>
            {/* ⚠ THE COUNT LINE IS NOT A CONTROL, and it used to disappear with the button:
                the whole header rode `canEdit` (`manage:patients`) while the button inside it
                was DISABLED on `canCreateSoapNote`. Both halves were wrong — a clinical add
                gated on the patient slug, and a control disabled rather than hidden. The count
                now always shows and the button is absent unless the person holds what
                POST /soap-notes requires. */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {searchFilteredRecords.soap.length} note{searchFilteredRecords.soap.length !== 1 ? 's' : ''}
              </Typography>
              {canCreateSoapNote && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddEntry('soap')}
                >
                  New SOAP Note
                </Button>
              )}
            </Box>
            {searchFilteredRecords.soap.length > 0 ? (
              searchFilteredRecords.soap.map(soapNote => (
                <SoapNoteCard
                  key={soapNote.id}
                  soapNote={soapNote}
                  onView={handleViewSoapNote}
                  onEdit={handleEditSoapNote}
                  onMenu={canArchive ? handleSoapNoteMenuOpen : undefined}
                  /* PATCH /soap-notes/:id -> manage:medical_records, not manage:patients. */
                  canEdit={can.editSoapNote}
                  attachmentCount={soapAttachmentCounts[soapNote.id] || 0}
                />
              ))
            ) : (
              <Box py={4} textAlign="center">
                <Typography color="textSecondary">No SOAP notes recorded</Typography>
              </Box>
            )}
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={6}>
            {!panels.labs && (
              <Box data-testid="chart-no-access-lab">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.labs.noun)} />
              </Box>
            )}
            {panels.labs && (
              <Stack spacing={2}>
                {horseId && <LabsDiagnosticsPanel horseId={horseId} />}
              </Stack>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {!panels.medications && (
              <Box data-testid="chart-no-access-tab2">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.medications.noun)} />
              </Box>
            )}
            {panels.medications && (<>
            {/* POST /medications requires manage:medical_records — see canAddClinical. The refill
                request beside it rides the same block: it is a clinical order, not a front-desk task. */}
            {canAddClinical && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {searchFilteredRecords.medication.length} group{searchFilteredRecords.medication.length !== 1 ? 's' : ''}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setLogRefillOpen(true)}
                  >
                    Log Refill Request
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddEntry('medication')}
                  >
                    Add Medication
                  </Button>
                </Stack>
              </Box>
            )}
            {searchFilteredRecords.medication.length > 0 ? (
              searchFilteredRecords.medication.map(group => {
                const lowStockNames = lowStockItems.map((item: any) => item.name);
                return <MedicationCard key={group.date} medications={group.medications} date={group.date} lowStockItems={lowStockNames} totalActiveMedications={totalActiveMedications} horseId={horseId} />;
              })
            ) : (
              <Box py={4} textAlign="center">
                <Typography color="textSecondary">No medications recorded</Typography>
              </Box>
            )}
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {!panels.procedures && (
              <Box data-testid="chart-no-access-tab3">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.procedures.noun)} />
              </Box>
            )}
            {panels.procedures && (<>
            {/* POST /procedures requires manage:medical_records — see canAddClinical. */}
            {canAddClinical && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {searchFilteredRecords.procedure.length} procedure{searchFilteredRecords.procedure.length !== 1 ? 's' : ''}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddEntry('procedure')}
                >
                  Add Procedure
                </Button>
              </Box>
            )}
            {searchFilteredRecords.procedure.length > 0 ? (
              searchFilteredRecords.procedure.map(record => (
                <ProcedureCard key={record.id} record={record} onEdit={handleEditProcedure} onMenu={canArchive ? handleProcedureMenuOpen : undefined} canEdit={can.editClinicalRecord} />
              ))
            ) : (
              <Box py={4} textAlign="center">
                <Typography color="textSecondary">No procedures recorded</Typography>
              </Box>
            )}
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            {!panels.vaccinations && (
              <Box data-testid="chart-no-access-tab4">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.vaccinations.noun)} />
              </Box>
            )}
            {panels.vaccinations && (<>
            {/* POST /vaccinations requires manage:medical_records — see canAddClinical. */}
            <PatientVaccinationsTab
              vaccinations={searchFilteredRecords.vaccination}
              allVaccinations={allVaccinations as any[]}
              catalogNames={vaccinationCatalog.filter((i) => i.isActive && i.clinicId).map((i) => i.name)}
              attachments={recordAttachmentMap}
              onAdd={canAddClinical ? () => handleAddEntry('vaccination') : undefined}
              onEdit={can.editClinicalRecord ? setEditingVaccination : undefined}
              onMenu={canArchive ? handleVaccinationMenuOpen : undefined}
            />
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={7}>
            {!panels.dischargeSummaries && (
              <Box data-testid="chart-no-access-tab7">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.dischargeSummaries.noun)} />
              </Box>
            )}
            {panels.dischargeSummaries && (<>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {(dischargeSummaries as any[]).length} discharge summar{(dischargeSummaries as any[]).length !== 1 ? 'ies' : 'y'}
              </Typography>
              {/* POST /discharge-summaries -> manage:medical_records (:42). Reaching this tab
                  only proves the person may READ discharge summaries; the panel gate above is
                  `view:medical_records` and this button is a write. */}
              {can.dischargeWrite && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => { setDischargeInitialId(null); setShowDischargeDialog(true); }}
                >
                  New Discharge
                </Button>
              )}
            </Box>
            {(dischargeSummaries as any[]).length === 0 ? (
              <Box py={4} textAlign="center">
                <Typography color="textSecondary">No discharge summaries yet</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(dischargeSummaries as any[])
                  .slice()
                  .sort((a: any, b: any) =>
                    new Date(b.dischargeDateTime || b.admissionDateTime || b.createdAt).getTime() -
                    new Date(a.dischargeDateTime || a.admissionDateTime || a.createdAt).getTime()
                  )
                  .map((d: any) => (
                    <Paper
                      key={d.id}
                      variant="outlined"
                      sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => { setDischargeInitialId(d.id); setShowDischargeDialog(true); }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {d.status === 'DRAFT' ? 'Discharge Summary (Draft)' : 'Discharge Summary'}
                          </Typography>
                          {d.attendingVet && (
                            <Typography variant="caption" color="text.secondary">
                              Vet: {d.attendingVet}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {/* DischargeSummary.dischargeDateTime / admissionDateTime are INSTANTS
                                (DateTimePicker + `new Date()` on finalize) — the clinic zone's day. */}
                            {d.dischargeDateTime
                              ? formatInstant(d.dischargeDateTime, INSTANT_FORMATS.date)
                              : d.admissionDateTime
                              ? `Admitted ${formatInstant(d.admissionDateTime, INSTANT_FORMATS.date)}`
                              : '—'}
                          </Typography>
                          <Chip label={d.status} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        </Box>
                      </Box>
                    </Paper>
                  ))}
              </Box>
            )}
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={8}>
            {/* The refusal, once the read has ANSWERED without view:billing. Classic carried no
                read gate at all on this tab — the panel simply rendered "No invoices yet" to a
                person who may not see the money, which is the same false statement the timeline
                was making about records. `permissionsPending` is excluded so it never races the
                mounted-and-disabled button below it. */}
            {!panels.invoices && !permissionsPending && (
              <Box data-testid="chart-no-access-invoice">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.invoices.noun)} />
              </Box>
            )}
            {(panels.invoices || permissionsPending) && (<>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {horseInvoiceStats?.totalInvoiceCount ?? 0} invoice{(horseInvoiceStats?.totalInvoiceCount ?? 0) !== 1 ? 's' : ''}
                {/* A5c: money.ts owns the format (.claude/rules/frontend.md) — the same
                    balance renders on PatientHeaderCard above through formatDollars, and a
                    raw toFixed would print it here without the thousands separator. The
                    in-flight caption on THIS layout lives on the card's Balance chip. */}
                {(horseInvoiceStats?.balance || 0) > 0 && (
                  <Typography component="span" sx={{ ml: 1, color: 'error.main', fontWeight: 600 }}>
                    · {formatDollars(horseInvoiceStats?.balance ?? 0)} outstanding
                  </Typography>
                )}
              </Typography>
              {/* P6: same mounted-and-disabled rule as the header Edit button. ⚠ The
                  showBilling prop on PatientHeaderCard above deliberately does NOT get
                  this arm: those two StatChips print a money figure, so mounting them
                  before the read answers would show a balance to someone the read may
                  be about to refuse. For a CONTROL the safe pop is OUT, for DATA it is
                  IN — that asymmetry is the whole rule here. */}
              {(canManageBilling || permissionsPending) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleGenerateInvoice}
                  disabled={!canManageBilling}
                >
                  New Invoice
                </Button>
              )}
            </Box>
            {!horseInvoiceList?.data?.length ? (
              <Box py={4} textAlign="center">
                <Typography color="textSecondary">No invoices yet</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(horseInvoiceList.data as any[]).map((inv: any) => {
                  const status = String(inv.status || '').toLowerCase();
                  // Invoice has no `date` scalar — the old `inv.date ||` arm was dead. What is
                  // left is Invoice.issuedAt (`@default(now())`), an INSTANT, so this is the
                  // clinic zone's day, not the browser's. (Invoice.dueDate is the CALENDAR one;
                  // it is not rendered here.)
                  const invoiceDate = inv.issuedAt || inv.createdAt;
                  return (
                    <Paper
                      key={inv.id}
                      data-testid="horse-invoice-row"
                      variant="outlined"
                      sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate('/billing', { state: { invoiceId: inv.id } })}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Invoice #{inv.invoiceNumber || inv.id.slice(0, 8)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" data-testid="horse-invoice-date">
                            {formatInstant(invoiceDate, INSTANT_FORMATS.date) || '—'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                          <Typography variant="body2" fontWeight={600}>
                            ${Number(inv.total ?? inv.amount ?? 0).toFixed(2)}
                          </Typography>
                          <Chip
                            label={inv.status}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: status === 'paid' ? '#E8F5E9' : status === 'overdue' ? '#FFEBEE' : '#FFF8E1',
                              color: status === 'paid' ? '#2E7D32' : status === 'overdue' ? '#C62828' : '#F57F17',
                            }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={9}>
            {!panels.communications && (
              <Box data-testid="chart-no-access-communication">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.communications.noun)} />
              </Box>
            )}
            {panels.communications && (<>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {clientCommunications.length} communication{clientCommunications.length !== 1 ? 's' : ''}
              </Typography>
              {/* POST /client-communications -> manage:medical_records (:27). */}
              {can.addClinicalRecord && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setClientCommDialogOpen(true)}
              >
                Log Communication
              </Button>
              )}
            </Box>
            {/* ⚠ `editClinicalRecord`, not `removeClinicalRecord`: DELETE
                /client-communications/:id declares `manage:medical_records` ALONE (:60), where
                the med / vaccination / procedure deletes declare that slug AND the archive one.
                The list renders its row Delete only when given a handler, so the prop is the gate. */}
            <ClientCommunicationList
              communications={clientCommunications}
              onDelete={can.editClinicalRecord ? (id) => {
                setConfirmAction({
                  message: 'Are you sure you want to delete this communication log?',
                  onConfirm: () => deleteClientCommunication.mutate(id),
                });
              } : undefined}
            />
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={10}>
            {!panels.problems && (
              <Box data-testid="chart-no-access-problem">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.problems.noun)} />
              </Box>
            )}
            {panels.problems && horseId && <ProblemListPanel horseId={horseId} />}
          </TabPanel>

          <TabPanel value={tabValue} index={11}>
            {!panels.recalls && (
              <Box data-testid="chart-no-access-recall">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.recalls.noun)} />
              </Box>
            )}
            {panels.recalls && horseId && <RecallRemindersPanel horseId={horseId} />}
          </TabPanel>

          <TabPanel value={tabValue} index={12}>
            {!panels.treatmentSheets && (
              <Box data-testid="chart-no-access-treatment-sheet">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.treatmentSheets.noun)} />
              </Box>
            )}
            {panels.treatmentSheets && (<>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {(horseTreatmentSheets as any[]).length} treatment sheet{(horseTreatmentSheets as any[]).length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            {(horseTreatmentSheets as any[]).length === 0 ? (
              <Box py={4} textAlign="center">
                <Typography color="textSecondary">No treatment sheets for this patient</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(horseTreatmentSheets as any[])
                  .slice()
                  .sort((a: any, b: any) => new Date(b.admitDate).getTime() - new Date(a.admitDate).getTime())
                  .map((ts: any) => (
                    <Paper
                      key={ts.id}
                      variant="outlined"
                      sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/hospital/treatment-sheets/${ts.id}`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {ts.chiefComplaint || 'Treatment Sheet'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ts.attendingVet?.name ? `Vet: ${ts.attendingVet.name}` : ''}
                            {ts.stall?.name ? ` · ${ts.stall.name}` : ts.locationLabel ? ` · ${ts.locationLabel}` : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {/* TreatmentSheet.admitDate (`@default(now())`) and dischargeDate
                                (`new Date()` / a posted instant) are INSTANTS — clinic zone. */}
                            {formatInstant(ts.admitDate, INSTANT_FORMATS.date)}
                            {ts.dischargeDate ? ` – ${formatInstant(ts.dischargeDate, INSTANT_FORMATS.date)}` : ''}
                          </Typography>
                          <Chip
                            label={ts.status.charAt(0) + ts.status.slice(1).toLowerCase()}
                            size="small"
                            color={ts.status === 'ACTIVE' ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  ))}
              </Box>
            )}
            </>)}
          </TabPanel>

          <TabPanel value={tabValue} index={13}>
            {!panels.files && (
              <Box data-testid="chart-no-access-files">
                <NoAccess dense message={noAccessMessage(CHART_PANELS.files.noun)} />
              </Box>
            )}
            {panels.files && horseId && <PatientFilesTab horseId={horseId} onOpenSoapNote={handleViewSoapNote} />}
          </TabPanel>
        </Paper>

        {/* Archived Toggle */}
        <Button
          size="small"
          startIcon={<ArchiveIcon />}
          variant={showArchived ? 'contained' : 'outlined'}
          sx={{ ml: 2 }}
          onClick={() => setShowArchived(!showArchived)}
        >
          {showArchived ? 'Show Active Items' : 'Show Archived Items'}
        </Button>

        {/* Record Action Menu */}
        <Popover
          id="record-menu"
          open={Boolean(anchorPos)}
          anchorReference="anchorPosition"
          anchorPosition={anchorPos || undefined}
          onClose={handleRecordMenuClose}
        >
          <List sx={{ py: 0, minWidth: 200 }}>
            {(() => {
              const soapNote = soapNotes.find(s => s.id === selectedRecordId);
              const procedure = procedures.find(p => p.id === selectedRecordId);
              const vaccination = filteredVaccinations.find((v: any) => v.id === selectedRecordId);
              const medRecord = records.find(r => r.id === selectedRecordId);
              
              if (soapNote) {
                return (
                  <>
                    {canArchive && (
                      <ListItemButton 
                        onClick={() => {
                          if (selectedRecordId) handleArchiveSoapNote(selectedRecordId);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          <ArchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Archive SOAP Note" />
                      </ListItemButton>
                    )}
                  </>
                );
              }

              if (procedure) {
                return (
                  <>
                    {canArchive && (
                      <ListItemButton 
                        onClick={() => {
                          if (selectedRecordId) handleArchiveProcedure(selectedRecordId);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          <ArchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Archive Procedure" />
                      </ListItemButton>
                    )}
                  </>
                );
              }
              
              if (vaccination) {
                return (
                  <>
                    {canArchive && (
                      <ListItemButton 
                        onClick={() => {
                          if (selectedRecordId) handleArchiveVaccination(selectedRecordId);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          <ArchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Archive Vaccination" />
                      </ListItemButton>
                    )}
                  </>
                );
              }

              if (medRecord) {
                return (
                  <>
                    {canArchive && (
                      <ListItemButton 
                        onClick={() => {
                          if (selectedRecordId) handleArchiveRecord(selectedRecordId);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          <ArchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Archive Record" />
                      </ListItemButton>
                    )}
                  </>
                );
              }
              
              return null;
            })()}
          </List>
        </Popover>

        {/* Alert Management Dialog */}
        {horse && (
          <AlertManagementDialog
            open={alertsDialogOpen}
            onClose={() => setAlertsDialogOpen(false)}
            horseId={horseId!}
            horseName={horse.name}
          />
        )}

        {/* Discharge Summary Dialog */}
        {showDischargeDialog && horse && (
          <Dialog
            open={showDischargeDialog}
            onClose={() => setShowDischargeDialog(false)}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                m: { xs: 0, sm: 2 },
                maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
              },
            }}
          >
            <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              {dischargeInitialId ? 'Discharge Summary' : 'Discharge Summaries'}
            </DialogTitle>
            <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
              <DischargeSummarySimplified
                horseId={horse!.id}
                initialSummaryId={dischargeInitialId ?? undefined}
              />
            </DialogContent>
            <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Button 
                onClick={() => setShowDischargeDialog(false)}
                fullWidth={isMobile}
                sx={{ minHeight: 44 }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Invoice Notification Snackbar */}
        <Dialog
          open={invoiceNotification.open}
          onClose={() => setInvoiceNotification({ ...invoiceNotification, open: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {invoiceNotification.type === 'success' ? 'Invoice Created' : 'Error'}
          </DialogTitle>
          <DialogContent>
            <Typography>{invoiceNotification.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInvoiceNotification({ ...invoiceNotification, open: false })}>
              Close
            </Button>
            {invoiceNotification.type === 'success' && invoiceNotification.invoiceId && (
              <Button variant="contained" onClick={handleViewInvoice}>
                View Invoice
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* SOAP Note Dialog */}
        {horse && (
          <SoapNoteDialog
            key={selectedSoapNote?.id || 'new-soap-note'} // Force re-render when note changes
            open={soapDialogOpen}
            onClose={handleCloseSoapDialog}
            horseId={horse!.id}
            horseName={horse!.name}
            horseBreed={formatBreed(horse)}
            existingNote={selectedSoapNote || undefined}
            onSave={handleSoapNoteSave}
          />
        )}

        {/* StableDocs: compile the chart into one PDF and send it. */}
        <ShareRecordsDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          horseId={horseId!}
          horseName={horse?.name}
        />

        {/* Medication Dialog */}
        <MedicationDialog
          open={medicationDialogOpen}
          onClose={() => setMedicationDialogOpen(false)}
          onSave={handleMedicationSave}
          lowStockItems={lowStockItems.map((item: any) => item.name)}
          showAll={clinicSettings?.showAllMedications ?? true}
          catalogItems={medicationCatalog.filter(i => i.isActive && i.clinicId)}
        />

        <LogRefillRequestDialog
          open={logRefillOpen}
          onClose={() => setLogRefillOpen(false)}
          preselectedHorseId={horseId}
          onCreated={() => queryClient.invalidateQueries(['refill-requests'])}
        />

        {/* Procedure Dialog */}
        <ProcedureDialog
          open={procedureDialogOpen}
          onClose={() => {
            setProcedureDialogOpen(false);
            setEditingProcedure(null);
          }}
          onSave={handleProcedureSave}
          currentUserName={profile?.name || user?.displayName || user?.email?.split('@')[0] || ''}
          existingProcedure={editingProcedure}
          showAll={clinicSettings?.showAllProcedures ?? true}
          catalogItems={procedureCatalog.filter(i => i.isActive && i.clinicId)}
        />

        {/* Vaccination Dialog */}
        <VaccinationDialog
          open={vaccinationDialogOpen}
          onClose={() => setVaccinationDialogOpen(false)}
          onSave={handleVaccinationSave}
          currentUserName={profile?.name || user?.displayName || user?.email?.split('@')[0] || ''}
          lowStockItems={lowStockItems.map((item: any) => item.name)}
          showAll={clinicSettings?.showAllVaccinations ?? true}
          catalogItems={vaccinationCatalog.filter(i => i.isActive && i.clinicId)}
        />

        {/* Correct a saved vaccination's lot or dose; the record only (ADR-0047). */}
        <EditVaccinationDialog
          vaccination={editingVaccination}
          horseId={horseId!}
          onClose={() => setEditingVaccination(null)}
        />

        {/* Visit Note Dialog */}
        <VisitNoteDialog
          open={visitNoteDialogOpen}
          onClose={() => {
            setVisitNoteDialogOpen(false);
            setEditingVisitNote(null);
          }}
          onSave={handleVisitNoteSave}
          horseId={horseId!}
          existingRecord={editingVisitNote}
          tagSuggestions={visitNoteTagSuggestions}
        />

        {/* Client Communication Dialog */}
        <ClientCommunicationDialog
          open={clientCommDialogOpen}
          onClose={() => setClientCommDialogOpen(false)}
          onSave={(data) => createClientCommunication.mutateAsync(data)}
          horseId={horseId!}
        />

        {/* Confirmation Dialog */}
        <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            <Typography>{confirmAction?.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button
              onClick={() => {
                confirmAction?.onConfirm();
                setConfirmAction(null);
              }}
              variant="contained"
              color="error"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default HorseProfileClassic;